import { Collapse, IconButton, Stack, Tooltip } from '@mui/material';
import { ChevronUp, Minus, X } from 'lucide-react';
import { ReactNode, useState } from 'react';
import { useIntl } from 'react-intl';

interface CollapsibleBottomPanelProps {
    header: ReactNode;
    headerActions?: ReactNode;
    children: ReactNode;
    collapsible?: boolean;
    defaultExpanded?: boolean;
    isExpanded?: boolean;
    onToggleExpanded?: () => void;
    onClose?: () => void;
}

export default function CollapsibleBottomPanel({
    header,
    headerActions,
    children,
    collapsible = true,
    defaultExpanded = true,
    isExpanded: controlledExpanded,
    onToggleExpanded: controlledOnToggle,
    onClose,
}: CollapsibleBottomPanelProps) {
    const intl = useIntl();
    const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);

    const isControlled = controlledExpanded !== undefined;
    const isExpanded = isControlled ? controlledExpanded : internalExpanded;

    const handleToggle = () => {
        if (controlledOnToggle) {
            controlledOnToggle();
        } else {
            setInternalExpanded((prev) => !prev);
        }
    };

    return (
        <Stack direction="column" sx={{ width: '100%', overflow: 'hidden' }}>
            <Stack
                direction="row"
                display="grid"
                gridTemplateColumns="1fr auto"
                alignItems="center"
                justifyContent="space-between"
                gap={1}
            >
                {header}
                <Stack direction="row" spacing={0.5}>
                    {headerActions}
                    {collapsible && (
                        <Tooltip
                            title={intl.formatMessage({
                                id: isExpanded ? 'collapse' : 'expand',
                            })}
                        >
                            <IconButton
                                size="small"
                                onClick={handleToggle}
                                sx={{ p: 0.25 }}
                                data-testid="collapsible-bottom-panel-toggle"
                                aria-label={intl.formatMessage({
                                    id: isExpanded ? 'collapse' : 'expand',
                                })}
                            >
                                {isExpanded ? (
                                    <Minus size={14} />
                                ) : (
                                    <ChevronUp size={14} />
                                )}
                            </IconButton>
                        </Tooltip>
                    )}
                    {onClose && (
                        <Tooltip title={intl.formatMessage({ id: 'close' })}>
                            <IconButton
                                size="small"
                                onClick={onClose}
                                sx={{ p: 0.25 }}
                                data-testid="close-bottom-panel"
                                aria-label={intl.formatMessage({ id: 'close' })}
                            >
                                <X size={12} />
                            </IconButton>
                        </Tooltip>
                    )}
                </Stack>
            </Stack>

            {collapsible ? (
                <Collapse in={isExpanded}>
                    <Stack mt={1} overflow="hidden">
                        {children}
                    </Stack>
                </Collapse>
            ) : (
                <Stack mt={1} overflow="hidden">
                    {children}
                </Stack>
            )}
        </Stack>
    );
}
